const MAX_SAMPLE_FRAMES = 10000;
const round = n => n == null ? null : Math.round(n * 100) / 100;
function distribution(values) {
  if (!values.length) return { meanMs: null, p50Ms: null, p95Ms: null, p99Ms: null, maxMs: null };
  const sorted = [...values].sort((a, b) => a - b);
  const percentile = p => sorted[Math.min(sorted.length - 1, Math.ceil(sorted.length * p) - 1)];
  return { meanMs: round(values.reduce((a, b) => a + b, 0) / values.length), p50Ms: round(percentile(.5)), p95Ms: round(percentile(.95)), p99Ms: round(percentile(.99)), maxMs: round(sorted.at(-1)) };
}
function summarize(records, { requested = records.length, timedOut = false, hiddenMs = 0, visibilityChanges = 0, hiddenFrames = 0, wallMs = 0 } = {}) {
  const cadence = distribution(records.map(r => r.ms));
  const cpu = distribution(records.map(r => r.cpuMs));
  const stages = {};
  for (const name of new Set(records.flatMap(r => Object.keys(r.stages)))) {
    stages[name] = distribution(records.map(r => r.stages[name]).filter(Number.isFinite));
  }
  const throttled = cadence.p50Ms > 40 && cpu.p50Ms < cadence.p50Ms * .5;
  const reasons = [];
  if (records.length < requested || !records.length) reasons.push('insufficient-frames');
  if (timedOut) reasons.push('timeout');
  if (visibilityChanges || hiddenMs || hiddenFrames) reasons.push('visibility-change');
  if (throttled) reasons.push('throttled-cadence');
  const valid = !reasons.length;
  return { frames: records.length, requestedFrames: requested, ...cadence, cpu, stages,
    stallsOver50: records.filter(r => r.ms > 50).length, valid, reasons, timedOut, throttled,
    fps: valid && cadence.meanMs > 0 ? round(1000 / cadence.meanMs) : null,
    wallMs: round(wallMs), visibility: { changes: visibilityChanges, hiddenMs: round(hiddenMs), hiddenFrames },
    counters: records.at(-1)?.counters ?? null,
    gpu: { available: false, ms: null, reason: 'GPU timestamp collection is not enabled; CPU submission is not GPU duration.' } };
}

export class FrameMonitor {
  constructor({ capacity = 4096, clock = () => performance.now() } = {}) {
    this.capacity = Math.max(1, Math.min(MAX_SAMPLE_FRAMES, Math.floor(capacity)));
    this.clock = clock;
    this.records = [];
    this.cursor = 0;
    this.lastStart = null;
    this.visible = true;
    this.hiddenAt = null;
    this.hiddenMs = 0;
    this.hiddenFrames = 0;
    this.visibilityChanges = 0;
    this.samplers = new Set();
  }
  setVisible(visible, now = this.clock()) {
    if (visible === this.visible) return;
    this.visible = visible;
    this.lastStart = null;
    this.visibilityChanges++;
    if (visible) {
      this.hiddenMs += Math.max(0, now - this.hiddenAt);
      this.hiddenAt = null;
    } else this.hiddenAt = now;
  }
  totalHidden(now = this.clock()) {
    return this.hiddenMs + (this.hiddenAt == null ? 0 : Math.max(0, now - this.hiddenAt));
  }
  record({ start, renderStart = start, end, stages = {}, counters = null }) {
    if (!this.visible) { this.hiddenFrames++; return null; }
    const previous = this.lastStart;
    this.lastStart = start;
    if (previous == null || start <= previous) return null;
    const record = { ms: start - previous, cpuMs: Math.max(0, end - start),
      stages: { ...stages, update: Math.max(0, renderStart - start), submit: Math.max(0, end - renderStart) }, counters };
    if (this.records.length < this.capacity) this.records.push(record);
    else { this.records[this.cursor] = record; this.cursor = (this.cursor + 1) % this.capacity; }
    for (const sampler of this.samplers) {
      sampler.records.push(record);
      if (this.clock() >= sampler.deadline) sampler.finish(true);
      else if (sampler.records.length >= sampler.frames) sampler.finish(false);
    }
    return record;
  }
  stats() {
    const ordered = this.cursor ? [...this.records.slice(this.cursor), ...this.records.slice(0, this.cursor)] : this.records;
    return summarize(ordered, { hiddenMs: this.totalHidden(), visibilityChanges: this.visibilityChanges, hiddenFrames: this.hiddenFrames });
  }
  sample(frames = 600, { timeoutMs = 30000 } = {}) {
    if (!Number.isInteger(frames) || frames < 1 || frames > MAX_SAMPLE_FRAMES) throw new RangeError('frame count must be an integer from 1 to 10000');
    if (this.samplers.size >= 4) throw new Error('At most four frame samplers can run concurrently');
    timeoutMs = Number.isFinite(timeoutMs) ? Math.max(1, Math.min(timeoutMs, 120000)) : 30000;
    const start = this.clock();
    const hidden = this.totalHidden(start);
    const changes = this.visibilityChanges;
    const hiddenFrames = this.hiddenFrames;
    const initiallyHidden = !this.visible;
    return new Promise(resolve => {
      const sampler = { frames, deadline: start + timeoutMs, records: [], finish: timedOut => {
        clearTimeout(sampler.timer);
        this.samplers.delete(sampler);
        const now = this.clock();
        resolve(summarize(sampler.records, { requested: frames, timedOut, wallMs: now - start,
          hiddenMs: this.totalHidden(now) - hidden, visibilityChanges: this.visibilityChanges - changes + Number(initiallyHidden), hiddenFrames: this.hiddenFrames - hiddenFrames }));
      } };
      sampler.timer = setTimeout(() => sampler.finish(true), timeoutMs);
      this.samplers.add(sampler);
    });
  }
}
